import dayjs, { parseWithWeekday } from '@/lib/dayjs'
import {
  getItineraryImages,
  getBooking,
  markNotificationsRead,
  getConsBioImage,
} from './services'
import { getBookingPassengers } from '@/modules/navigation/modals/payment-hub/make-payment/services.js'
import {
  transformItinerary,
  itineraryTransformForView,
  getPTFSuppliers,
} from './itinerary/itinerary.module'
import { supplierCodesFormat } from './alerts/alerts.module'
import { checkFeedbackExists } from './consultant/services'
import { titleCase } from '@/lib/utilities'

const getDefaultState = () => {
  return {
    booking: null,
    itineraryImages: null,
    bookingPassengers: null,
  }
}

const state = getDefaultState()

const getters = {
  getBooking: (state) => {
    return state.booking ? state.booking : null
  },
  getFil: (state) => {
    return state.booking ? state.booking.booking_information.fil : ''
  },
  getLiveFlights: (state) => {
    return state.booking ? state.booking.booking_information.dfi_menu : null
  },
  getCruise: (state) => {
    return state.booking ? state.booking.booking_information.crsbkg : null
  },
  getShowFeedback: (state) => {
    return state.booking ? state.booking.feedback_exists : null
  },
  getBookingNumber: (state) => {
    return state.booking ? state.booking.user_information.bookingNumber : null
  },
  getBookingContacts: (state) => {
    return state.booking ? state.booking.user_information.bkgcontacts : null
  },
  getBookingPassengers: (state) => {
    return state.bookingPassengers ? state.bookingPassengers : null
  },
  getBookingAddress: (state) => {
    return state.booking ? state.booking.user_information.bookingAddress : null
  },
  getNamesPlus: (state) => {
    return state.booking ? state.booking.user_information.names_plus : null
  },
  getBookingName: (state) => {
    if (state && state.booking && state.booking.booking_information) {
      return titleCase(
        `${state.booking.booking_information.lead_firstname} ${state.booking.booking_information.lead_surname}`,
      )
    }
    return null
  },
  getBioImages: (state) => {
    return state.booking.bioImages ? state.booking.bioImages : null
  },
  getBookingSurname: (state) => {
    return state.booking ? state.booking.booking_information.lead_surname : null
  },
  getStaff: (state) => {
    return state.booking
      ? state.booking.booking_information.staffbkg === 'Y'
      : false
  },
  getHoneymoon: (state) => {
    return state.booking ? state.booking.booking_information.honeymoon : null
  },
  getWeddingAbroad: (state) => {
    return state.booking
      ? state.booking.booking_information.weddingabroad
      : null
  },
  getDescLinks: (state) => {
    return state.booking ? state.booking.booking_information.desclinks : null
  },
  getDestination: (state) => {
    return state.booking ? state.booking.booking_information.destination : null
  },
  getHeroImage: (state) => {
    return state.booking ? state.booking.booking_information.hero_img : null
  },
  getSource: (state) => {
    if (
      state.booking &&
      state.booking.booking_information &&
      state.booking.booking_information.source
    ) {
      return state.booking.booking_information.source
    }
    return 'EXT'
  },
  getCountry: (state) => {
    if (!state.booking) {
      return null
    }
    const bookingCharacter =
      state.booking.user_information.bookingNumber.charAt(0)
    if (bookingCharacter.toUpperCase() === 'R') {
      return 'IE'
    }
    return 'UK'
  },
  getCurrency: (state) => {
    if (!state.booking) {
      return '£' // default sterling
    }
    const bookingCharacter =
      state.booking.user_information.bookingNumber.charAt(0)
    if (bookingCharacter.toUpperCase() === 'R') {
      return '€'
    }
    return '£'
  },
  getBookingInfo: (state) => {
    return state.booking ? state.booking.booking_information : null
  },
  getFirstFlightItineraryItem: (state) => {
    if (
      state.booking &&
      state.booking.itinerary_transformed &&
      state.booking.itinerary_transformed &&
      state.booking.itinerary_transformed.items &&
      state.booking.itinerary_transformed.items[0] &&
      state.booking.itinerary_transformed.items[0].data &&
      state.booking.itinerary_transformed.items[0].data[0] &&
      state.booking.itinerary_transformed.items[0].data[0].jsontype === 'flight'
    ) {
      return state.booking.itinerary_transformed.items[0].data[0]
    }
  },
  getPredepShow: (state) => {
    if (state.booking && state.booking.booking_information.predepshow) {
      if (state.booking.booking_information.predepshow === 'true') {
        return true
      }
    }
    return false
  },
  getItinerary: (state) => {
    return state.booking ? state.booking.itinerary_transformed : null
  },
  getItineraryRaw: (state) => {
    return state.booking ? state.booking.booking_itinerary : null
  },
  getTripStarted: (state) => {
    if (state.booking) {
      if (
        dayjs(new Date()) >
        dayjs(state.booking.booking_itinerary.itinerary[0].date)
      ) {
        return true
      }
      return false
    }
  },
  getTripEnded: (state) => {
    if (state.booking) {
      if (
        dayjs(new Date()) >
        parseWithWeekday(
          `${state.booking.booking_information.vtdate_return} 23:59`,
          'ddd DD MMM YYYY HH:mm',
        ).add(1, 'days')
      ) {
        return true
      }
      return false
    }
  },
  getAccount: (state) => {
    return state.booking ? state.booking.account_information : null
  },
  getCostInfo: (state) => {
    return state.booking ? state.booking.costing_information : null
  },
  getCityCodes: (state) => {
    return state.cityCodes ? state.cityCodes : null
  },
  getSupplierCodes: (state) => {
    return state.booking ? state.booking.supplierCodes : null
  },
  getDueDate: (state) => {
    return state.booking ? state.booking.account_information.duebyCost : null
  },
  getEssentialInfoMenu: (state) => {
    return state.booking ? state.booking.essential_information_menu : null
  },
  getEssentialInfo: (state) => {
    return state.booking ? state.booking.essential_information : null
  },
  getNPD: (state) => {
    if (state.booking) {
      if (state.booking.account_information.npd_next[0].due) {
        return state.booking.account_information.npd_next[0].date
      }
      return `by ${state.booking.account_information.duebyDate}`
    }
  },
  getBranchTelephone: (state) => {
    // This should get the contact phone number for the consultant branch the customer used to book
    if (
      state.booking &&
      state.booking.consultant_information &&
      state.booking.consultant_information.branchContact &&
      Array.isArray(state.booking.consultant_information.branchContact)
    ) {
      return state.booking.consultant_information.branchContact[0].line0.telno
    }
    return null
  },
  getTel1: (state) => {
    return state.booking
      ? state.booking.user_information.bookingAddress.tel.tel_01.number
      : null
  },
  getTel2: (state) => {
    return state.booking
      ? state.booking.user_information.bookingAddress.tel.tel_02.number
      : null
  },
  getTel3: (state) => {
    return state.booking
      ? state.booking.user_information.bookingAddress.tel.tel_03.number
      : null
  },
  getSessionID: (state) => {
    if (
      state.booking &&
      state.booking.user_information &&
      state.booking.user_information.sessionid
    ) {
      return state.booking.user_information.sessionid
    }
    return null
  },
  getEmail: (state) => {
    if (
      state.booking &&
      state.booking.user_information &&
      state.booking.user_information.email
    ) {
      return state.booking.user_information.email
    }
    return null
  },
  getBookingInformation: (state) => {
    return state.booking ? state.booking.booking_information : null
  },
  getAppInstall: (state) => {
    return state.booking
      ? state.booking.booking_information.app_installed
      : null
  },
  getConsultantInformation: (state) => {
    return state.booking ? state.booking.consultant_information : null
  },
  getConsultantImage: (state) => {
    return state.booking ? state.booking.consultant_information.image : null
  },
  getTripType: (state) => {
    return state.booking &&
      state.booking.booking_information &&
      state.booking.booking_information.holiday_type &&
      state.booking.booking_information.holiday_type.code
      ? state.booking.booking_information.holiday_type.code
      : null
  },
  getDepartureDate: (state) => {
    let departureDate = null
    if (
      state.booking &&
      state.booking.booking_itinerary &&
      state.booking.booking_itinerary.itinerary
    ) {
      const itinerary = state.booking.booking_itinerary.itinerary
      // loop over main itinerary blocks
      for (let i = 0; i < itinerary.length; i++) {
        // now loop over the data elements within each block
        for (let k = 0; k < itinerary[i].data.length; k++) {
          if (
            itinerary[i].data[k].jsontype !== 'insurance' &&
            itinerary[i].data[k].statusonrecord !== 'CANCELLED' &&
            itinerary[i].data[k].statusonrecord !== 'UNABLE TO CONFIRM' &&
            itinerary[i].title
          ) {
            departureDate = itinerary[i].title
            return departureDate
          }
        }
      }
    }
    return departureDate
  },
  getDeptDate: (state) => {
    if (
      state.booking &&
      state.booking.booking_itinerary &&
      state.booking.booking_itinerary.itinerary
    ) {
      const itinerary = state.booking.booking_itinerary.itinerary
      // loop over main itinerary blocks
      for (let i = 0; i < itinerary.length; i++) {
        // now loop over the data elements within each block
        for (let k = 0; k < itinerary[i].data.length; k++) {
          if (
            itinerary[i].data[k].jsontype !== 'insurance' &&
            itinerary[i].data[k].statusonrecord !== 'CANCELLED' &&
            itinerary[i].data[k].status !== 'cancelled' &&
            itinerary[i].data[k].statusonrecord !== 'UNABLE TO CONFIRM' &&
            itinerary[i].title
          ) {
            let flightCount = 0
            let deptDate = new Date(`${itinerary[i].title} 00:00`)
            if (
              itinerary[i].types.includes('flight') &&
              itinerary[i].data.length
            ) {
              for (const item of itinerary[i].data) {
                // Will take the first flight from the itinerary and use as its dept date and time.
                if (
                  item.jsontype === 'flight' &&
                  item.depdate &&
                  item.deptime &&
                  flightCount === 0
                ) {
                  if (item.deptime && item.deptime !== 'TBC') {
                    deptDate = new Date(`${item.depdate} ${item.deptime}`)
                  } else {
                    deptDate = new Date(`${item.depdate}`)
                  }
                  flightCount++
                }
              }
            }
            return deptDate
          }
        }
      }
    }
  },
  hasBookingSummary: (state) => {
    // Returns true if essential information contains an item called Booking Summary
    let hasSummary = false
    if (state.booking && state.booking.essential_information) {
      for (
        let i = 0;
        i < state.booking.essential_information.menu.length;
        i++
      ) {
        if (
          state.booking.essential_information.menu[i].title ===
          'Booking Summary'
        ) {
          hasSummary = true
          return hasSummary
        }
      }
    }
    return hasSummary
  },
  hasUnreadEmails: (state) => {
    let hasUnread = false
    if (
      state.booking &&
      state.booking.op_emails_notifications_unread === true
    ) {
      hasUnread = true
    }
    return hasUnread
  },
  getEmails: (state) => {
    return state.booking ? state.booking.op_emails : null
  },
  getRefundLetter: (state) => {
    return state.booking
      ? state.booking.op_emails_notifications_refund_letter
      : null
  },
  getItineraryJsonTypes: (state) => {
    if (
      state.booking &&
      state.booking.booking_itinerary &&
      state.booking.booking_itinerary.jsontypes
    ) {
      return state.booking.booking_itinerary.jsontypes
    }
    return null
  },
  getUserInfo: (state) => {
    return state.booking ? state.booking.user_information : null
  },
  consBioImages: (state) => {
    return state.booking ? state.booking.bioImages : null
  },
  getDestinationAdvice: (state) => {
    return state.booking ? state.booking.destination_advice : null
  },
  checkForAdditionalSupDocs: (state) => {
    if (
      state.booking &&
      state.booking.booking_itinerary &&
      state.booking.booking_itinerary.additionalsupdocs &&
      state.booking.booking_itinerary.additionalsupdocs[0] &&
      state.booking.booking_itinerary.additionalsupdocs[0]
        .additionalsupdocslist &&
      state.booking.booking_itinerary.additionalsupdocs[0]
        .additionalsupdocslist[0] &&
      state.booking.booking_itinerary.additionalsupdocs[0]
        .additionalsupdocslist[0].doclist &&
      state.booking.booking_itinerary.additionalsupdocs[0]
        .additionalsupdocslist[0].doclist.length
    ) {
      return (
        state.booking.booking_itinerary.additionalsupdocs[0]
          .additionalsupdocslist[0].doclist.length > 0
      )
    }
    return false
  },
  getAdditionalSupDocs: (state) => {
    if (
      state.booking &&
      state.booking.booking_itinerary &&
      state.booking.booking_itinerary.additionalsupdocs &&
      state.booking.booking_itinerary.additionalsupdocs[0] &&
      state.booking.booking_itinerary.additionalsupdocs[0]
        .additionalsupdocslist &&
      state.booking.booking_itinerary.additionalsupdocs[0]
        .additionalsupdocslist[0] &&
      state.booking.booking_itinerary.additionalsupdocs[0]
        .additionalsupdocslist[0].doclist &&
      state.booking.booking_itinerary.additionalsupdocs[0]
        .additionalsupdocslist[0].doclist.length > 0
    ) {
      const indexes = []
      state.booking.booking_itinerary.additionalsupdocs[0].additionalsupdocslist.forEach(
        (list) => list.doclist.forEach((index) => indexes.push(index)),
      )
      return indexes
    }
    return []
  },
  getAdditionalSupDocGroups: (state) => {
    if (
      state.booking &&
      state.booking.booking_itinerary &&
      state.booking.booking_itinerary.additionalsupdocs &&
      state.booking.booking_itinerary.additionalsupdocs[0] &&
      state.booking.booking_itinerary.additionalsupdocs[0]
        .additionalsupdocslist &&
      state.booking.booking_itinerary.additionalsupdocs[0]
        .additionalsupdocslist[0] &&
      state.booking.booking_itinerary.additionalsupdocs[0].additionalsupdocslist
        .length > 0
    ) {
      return state.booking.booking_itinerary.additionalsupdocs[0]
        .additionalsupdocslist
    }
    return []
  },
  isBookingCancelled: (state) => {
    return state.booking.booking_information.booking_cancelled
  },
}

const actions = {
  async loadBookingAction({ commit, dispatch }, loadBookingData) {
    if (!loadBookingData || !loadBookingData.name || !loadBookingData.bookno)
      return false
    const booking = await getBooking(
      loadBookingData.name,
      loadBookingData.bookno,
      loadBookingData.source,
    )
    if (booking && booking.booking_itinerary) {
      booking.itinerary_transformed = transformItinerary(
        booking.booking_itinerary,
        booking.booking_itinerary.itinerary,
      )
      booking.itinerary_transformed = itineraryTransformForView(
        booking.itinerary_transformed,
        booking.booking_itinerary.notes,
        booking.booking_itinerary.additionaldocs,
      )
    } else {
      // booking not found, or the API reports it's still being processed - either
      // way, surface the real message where we have one and log the user out
      dispatch(
        'flashMessage/set',
        {
          type: 'error',
          message:
            (booking && booking.login && booking.login.msg) ||
            'Your booking is being worked on and is temporarily unavailable. Please try again later.',
        },
        { root: true },
      )
      // reset state now rather than after a delay, but leave navigation to the
      // caller (it already redirects based on this action's return value) so we
      // don't race it with a second, redundant push to /login
      dispatch('auth/logoutAction', true, { root: true })
      return false
    }

    if (booking && booking.booking_itinerary) {
      commit('bookingReceived', booking)
      dispatch('loadItineraryImagesAction', booking)
      dispatch('getConsBioImages', booking)
      dispatch('checkFeedBack', booking)
      commit('appInstalledReceived', booking.booking_information.app_installed)
      dispatch('loadSupplierCodes', booking)
      dispatch('getBookingPassengersLookup', booking)
      dispatch('checkForTouchdownFlights', booking.booking_itinerary)
    }
    if (booking && booking.essential_information_menu) {
      dispatch(
        'essentialInfoMenu/createMenu',
        booking.essential_information_menu,
        { root: true },
      )
    }
    return true
  },
  bookingReceivedAction({ commit, dispatch }, booking) {
    dispatch('booking/loadItineraryImagesAction', booking, { root: true })
    commit('bookingReceived', booking)
    dispatch('setItineraryAction', booking)
    dispatch('loadSupplierCodes', booking)
  },
  async loadItineraryImagesAction({ commit }, booking) {
    let cities = []
    if (
      booking &&
      booking.booking_information &&
      booking.booking_information.citylink &&
      booking.booking_information.citylink[0]
    ) {
      cities = booking.booking_information.citylink
    }
    if (
      booking &&
      booking.booking_itinerary &&
      booking.booking_itinerary.jsontypes.indexOf('tour') >= 0
    ) {
      const suppcodes = getPTFSuppliers(booking.booking_itinerary.itinerary)
      if (suppcodes.length >= 1) {
        cities = [...cities, ...suppcodes]
      }
    }

    if (!cities || cities.length === 0) {
      return commit('itineraryImagesReceived', null)
    } else {
      commit('cityCodesReceived', cities)
    }
    const data = {
      city: cities,
      width: 954,
    }
    const res = await getItineraryImages(data)
    let resData = null
    if (res && res.data && res.data[0]) {
      resData = res.data.map((item) => item.href)
    }
    if (resData && state.booking.booking_information.hero_img) {
      resData.unshift(state.booking.booking_information.hero_img)
    }
    commit('itineraryImagesReceived', resData)
  },
  loadSupplierCodes({ commit }, booking) {
    const codes = supplierCodesFormat(booking.booking_itinerary.itinerary)
    commit('supplierCodesReceived', codes)
  },
  resetStateAction({ commit }) {
    commit('resetState')
  },
  async markNotificationsRead({ commit }) {
    await markNotificationsRead(state.booking.user_information.bookingNumber)
    commit('markNotificationsRead')
  },
  async getBookingPassengersLookup({ commit }, booking) {
    const data = await getBookingPassengers(
      booking.user_information.token,
      booking.user_information.bookingNumber,
    )
    commit('setBookingPassengersInfo', data)
  },
  async getConsBioImages({ commit }, booking) {
    const data = await getConsBioImage(booking.booking_information.logname)
    commit('consBioImagesReceived', data)
  },
  async checkFeedBack({ commit }, booking) {
    const data = await checkFeedbackExists(
      booking.user_information.bookingNumber,
      booking.booking_information.fil,
    )
    commit('feedbackExists', {
      data: data,
      date: booking.booking_information.vtdate_authorised,
    })
  },
  checkForTouchdownFlights({ commit }, bookingItinerary) {
    commit('touchdownFlightsReceived', bookingItinerary.flightsWithTouchdowns)
  },
}

const mutations = {
  bookingReceived(state, booking) {
    booking.numDaysSinceAuth = 0
    const authPlusTen = new Date(booking.booking_information.vtdate_authorised)
    const today = new Date()
    const Difference_In_Time = today.getTime() - authPlusTen.getTime()
    const Difference_In_Days = Difference_In_Time / (1000 * 3600 * 24)
    booking.numDaysSinceAuth = Math.round(Difference_In_Days)
    state.booking = booking
  },
  itineraryImagesReceived(state, images) {
    state.itineraryImages = images
  },
  cityCodesReceived(state, cities) {
    state.cityCodes = [...cities]
  },
  supplierCodesReceived(state, supplierCodes) {
    state.booking.supplierCodes = supplierCodes
  },
  appInstalledReceived(state, _val) {
    state.app_installed = _val
  },
  consBioImagesReceived(state, images) {
    state.booking.bioImages = images
  },
  setBookingPassengersInfo(state, passengers) {
    state.bookingPassengers = passengers
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  },
  markNotificationsRead(state) {
    state.booking.op_emails_notifications_unread = false
  },
  feedbackExists(state, feedback) {
    state.booking.feedback_exists = true
    if (feedback.data.length >= -1) state.booking.feedback_exists = false
    const _tmp = new Date(feedback.date)
    if (_tmp) {
      // 2 weeks ago
      let _now = new Date()
      _now = _now - 864000000
      if (_tmp < _now) {
        state.booking.feedback_exists = false
      }
    }
  },
  touchdownFlightsReceived(state, touchdownFlights) {
    state.booking.touchdownFlights = touchdownFlights
  },
}

export default {
  namespaced: true,
  getters,
  state,
  actions,
  mutations,
}
